import type { VisualPayload, EggPoolEntry } from "../../types";
import CardShell from "./_CardShell";
import PokemonSprite from "../PokemonSprite";

interface Props { payload: VisualPayload }

// Per-egg-type accent so 12 km / 10 km (the rare drops) read at a
// glance and 2 km / 5 km stay muted.  Mirrors the visual hierarchy
// pattern in RaidBossesCard (5-Star/Mega get warmer colors).
const EGG_ACCENT: Record<string, { border: string; label: string; pill: string }> = {
  "12 km": {
    border: "border-rose-500/30",
    label: "text-rose-300",
    pill: "bg-rose-500/15 border-rose-500/25 text-rose-300",
  },
  "10 km": {
    border: "border-amber-500/30",
    label: "text-amber-300",
    pill: "bg-amber-500/15 border-amber-500/25 text-amber-300",
  },
  "7 km": {
    border: "border-pink-500/25",
    label: "text-pink-300",
    pill: "bg-pink-500/10 border-pink-500/20 text-pink-300",
  },
  "5 km": {
    border: "border-blue-500/20",
    label: "text-blue-300",
    pill: "bg-blue-500/10 border-blue-500/20 text-blue-300",
  },
  "2 km": {
    border: "border-emerald-500/20",
    label: "text-emerald-300",
    pill: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
  },
  "1 km": {
    border: "border-white/15",
    label: "text-white/55",
    pill: "bg-white/5 border-white/15 text-white/60",
  },
};

const FALLBACK_ACCENT = {
  border: "border-white/15",
  label: "text-white/55",
  pill: "bg-white/5 border-white/15 text-white/60",
};

/** ShinySparkle — small star icon next to shiny-capable Pokémon. */
function ShinySparkle() {
  return (
    <span
      aria-label="shiny available"
      title="Shiny available"
      className="text-yellow-300 text-[11px]"
    >
      ✦
    </span>
  );
}

/** Tiny inline badge used for adventure-sync / regional / gift-exchange flags. */
function Badge({ label, title, className }: { label: string; title: string; className: string }) {
  return (
    <span
      title={title}
      aria-label={title}
      className={`text-[9px] uppercase tracking-wider px-1 py-px rounded border ${className}`}
    >
      {label}
    </span>
  );
}

function CpRange({ egg }: { egg: EggPoolEntry }) {
  if (egg.cp_min == null || egg.cp_max == null) return null;
  return (
    <span className="text-[10px] text-white/40 tabular-nums shrink-0">
      {egg.cp_min.toLocaleString()}–{egg.cp_max.toLocaleString()}
    </span>
  );
}

/**
 * "What's hatching?" — current egg pool grouped by hatch distance.
 *
 * Rare distances (12 km, 10 km) get warmer accent colors so they
 * pop visually; 2 km / 5 km stay muted.  Each Pokémon row shows
 * shiny availability, CP range, and inline badges for
 * Adventure-Sync-only / regional / gift-exchange-only hatches —
 * critical context for trainers who want to know whether the
 * Pokémon is grindable from regular eggs or restricted.
 *
 * Source: LeekDuck via ScrapedDuck (attribution footer required
 * per ScrapedDuck's licence terms).
 *
 * Phase 3.
 */
export default function EggsTodayCard({ payload }: Props) {
  const sections = payload.sections ?? [];
  const summarySec = sections.find((s) => s.section === "egg_summary");
  const byTypeSec = sections.find((s) => s.section === "eggs_by_type");
  const byType = (byTypeSec?.by_egg_type as Record<string, EggPoolEntry[]> | undefined) ?? {};
  const typeCounts = (summarySec?.type_counts as Record<string, number> | undefined) ?? {};
  const totalEggs = (summarySec?.total_eggs as number | undefined) ?? 0;
  const shinyCount = (summarySec?.shiny_count as number | undefined) ?? 0;

  const typeEntries = Object.entries(byType);

  if (typeEntries.length === 0) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined} accent="border-amber-500/20">
        <p className="text-sm text-white/55 py-2">No current egg pool data available.</p>
      </CardShell>
    );
  }

  return (
    <CardShell
      title={payload.title}
      source={payload.data_source}
      timestamp={payload.data_timestamp}
      sourceUrl={payload.source_url}
      shareUrl={payload.share_url}
      snapshotPayload={payload.snapshot_eligible ? payload : undefined}
      accent="border-amber-500/30"
    >
      {/* Top strip: total + shiny count + per-distance chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-white/[0.06]">
        <span className="text-sm text-white/70">
          {totalEggs} species hatching
        </span>
        {shinyCount > 0 && (
          <span className="text-[11px] text-yellow-300/80 flex items-center gap-0.5">
            <ShinySparkle /> {shinyCount} shiny-capable
          </span>
        )}
        <span className="text-white/20" aria-hidden="true">·</span>
        {Object.entries(typeCounts).map(([eggType, n]) => {
          const accent = EGG_ACCENT[eggType] ?? FALLBACK_ACCENT;
          return (
            <span
              key={eggType}
              className={`text-[10px] px-1.5 py-0.5 rounded border ${accent.pill}`}
            >
              {eggType}: {n}
            </span>
          );
        })}
      </div>

      {/* Per-distance sections */}
      <div className="space-y-4">
        {typeEntries.map(([eggType, eggs]) => {
          const accent = EGG_ACCENT[eggType] ?? FALLBACK_ACCENT;
          return (
            <section key={eggType}>
              <h4 className={`text-[11px] font-semibold uppercase tracking-widest mb-2 ${accent.label}`}>
                {eggType} ({eggs.length})
              </h4>
              <ul className="space-y-1.5">
                {eggs.map((e, i) => (
                  <li
                    key={i}
                    className={`flex sm:items-baseline gap-2 text-xs border-l-2 pl-2.5 py-0.5 ${accent.border}`}
                  >
                    <PokemonSprite src={e.image ?? undefined} alt={e.name} size="sm" />
                    <span className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 flex-1 min-w-0">
                      <span className="flex items-center gap-1.5 sm:min-w-[10rem] shrink-0">
                        <span className="text-white/85 font-medium">{e.name}</span>
                        {e.can_be_shiny && <ShinySparkle />}
                      </span>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:flex-1">
                        {e.is_adventure_sync && (
                          <Badge
                            label="Adv Sync"
                            title="Adventure Sync only — must walk to earn"
                            className="bg-sky-500/10 text-sky-300 border-sky-500/25"
                          />
                        )}
                        {e.is_regional && (
                          <Badge
                            label="Regional"
                            title="Region-restricted egg pool"
                            className="bg-orange-500/10 text-orange-300 border-orange-500/25"
                          />
                        )}
                        {e.is_gift_exchange && (
                          <Badge
                            label="Gift"
                            title="From Gift exchanges only"
                            className="bg-purple-500/10 text-purple-300 border-purple-500/25"
                          />
                        )}
                        <CpRange egg={e} />
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      {/* Attribution footer — required per ScrapedDuck licence */}
      <div className="mt-4 pt-3 border-t border-white/[0.06]">
        <p className="text-[11px] text-white/35">
          Egg pool data from{" "}
          <a
            href="https://leekduck.com/eggs/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400/70 hover:text-amber-300 transition-colors"
          >
            LeekDuck
          </a>{" "}
          via{" "}
          <a
            href="https://github.com/bigfoott/ScrapedDuck"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400/70 hover:text-amber-300 transition-colors"
          >
            ScrapedDuck
          </a>
        </p>
      </div>
    </CardShell>
  );
}
